/**
 * This hooks combines trip templates and multiload trip templates into a single
 * list of "typed" trip templates. The list renderer is then able to render
 * everything together, with enough information to render each type.
 */

import { get, sortBy } from 'lodash-es';
import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';

import { RouteMultiloadTemplateRead } from 'src/common/external/bambi-api/routesApi';

import {
  MultiloadPickupDropoffTripTemplateOrderItem,
  MultiLoadTripTemplate,
  RouteMultiloadTemplateFormValues,
  RouteTripTemplateFormValues,
  SingleLoadTripTemplate,
} from './types';

export function useTripTemplatesList(
  tripTemplates: (RouteTripTemplateFormValues & { __id: string })[],
  multiloadTripTemplates: RouteMultiloadTemplateRead[]
) {
  if (!tripTemplates.length && !multiloadTripTemplates.length) {
    return {
      tripTemplates: [],
      multiloadTripTemplates: [],
      tripTemplatesMap: {},
      rawTripTemplates: tripTemplates,
      renderableTripTemplates: [],
    };
  }

  // Create map of trip templates location detail id to trip template
  const tripTemplatesMap = tripTemplates.reduce((acc, tripTemplate) => {
    acc[tripTemplate.dropoff.id] = {
      tripTemplate,
      type: 'dropoff',
    };
    acc[tripTemplate.pickup.id] = {
      tripTemplate,
      type: 'pickup',
    };
    return acc;
  }, {} as Record<string, { tripTemplate: RouteTripTemplateFormValues & { __id: string }; type: 'pickup' | 'dropoff' }>);

  const usedTripTemplateIds = new Set<string>();

  // Create map of trip templates that are within the multiloadTripTemplates
  const multiloadTripTemplatesMap = multiloadTripTemplates.reduce(
    (acc, multiloadTripTemplate) => {
      return {
        ...acc,
        [multiloadTripTemplate.id]:
          multiloadTripTemplate.template_trip_location_details.map((detail) => {
            const foundTripTemplate = get(
              tripTemplatesMap,
              detail.trip_location_detail_id,
              null
            );

            if (!foundTripTemplate) {
              return null;
            }
            usedTripTemplateIds.add(foundTripTemplate.tripTemplate.__id);
            return foundTripTemplate;
          }),
      };
    },
    {} as Record<
      string,
      ({
        tripTemplate: (RouteTripTemplateFormValues & { __id: string }) | null;
        type: 'pickup' | 'dropoff';
      } | null)[]
    >
  );

  // Filter list of trip templates to only include those that are not in the multiloadTripTemplates
  const filteredTripTemplates = tripTemplates.filter(
    (tripTemplate) => !usedTripTemplateIds.has(tripTemplate.__id)
  );

  // Generate list of multiload trip templates that are able to be rendered
  const multiloadTemplates = multiloadTripTemplates.map((multiloadTemplate) => {
    const orderedTripTemplates: (MultiloadPickupDropoffTripTemplateOrderItem | null)[] =
      multiloadTemplate.template_trip_location_details.map(
        (tripLocationDetail, i) => {
          const tripTemplate = get(
            tripTemplatesMap,
            tripLocationDetail.trip_location_detail_id,
            null
          );

          if (!tripTemplate) {
            return null;
          }

          return {
            id: `${tripTemplate.tripTemplate.__id}-${tripTemplate.type}`,
            trip: tripTemplate.tripTemplate,
            type: tripTemplate.type,
            scheduledAt: tripTemplate.tripTemplate.scheduled_pickup_time,
            order: 1 + i,
          };
        }
      );

    return {
      // We don't have an ID when creating in state, so we generate a random one
      // to ensure the item has a unique key. May need to revisit this if we're
      // getting a bunch of weird renders.
      __id: !multiloadTemplate.id ? uuidv4() : multiloadTemplate.id,
      type: 'multi-load' as MultiLoadTripTemplate,
      configuration: multiloadTemplate,
      // Ordered trip templates list
      trip_templates: orderedTripTemplates,
    };
  });

  const singleLoadTemplates = filteredTripTemplates.map((tripTemplate) => {
    return {
      ...tripTemplate,
      type: 'single-load' as SingleLoadTripTemplate,
    };
  });

  // Combine single-load and multiload templates
  const renderableTripTemplates = [
    ...(singleLoadTemplates as Array<
      RouteTripTemplateFormValues & {
        type: SingleLoadTripTemplate;
        __id: string;
      }
    >),
    ...multiloadTemplates,
  ];

  // Sort trip templates by scheduled_pickup_time, using the first trip template in a multi-load template
  const sortedTripTemplates = sortBy(renderableTripTemplates, (item) => {
    let scheduledAt = '';
    if (item.type === 'multi-load') {
      scheduledAt = (item as unknown as RouteMultiloadTemplateFormValues)
        .trip_templates[0]?.scheduled_pickup_time;
    } else {
      scheduledAt = (item as RouteTripTemplateFormValues).scheduled_pickup_time;
    }

    return DateTime.fromISO(scheduledAt).toMillis();
  });

  return {
    tripTemplates: filteredTripTemplates,
    renderableTripTemplates: sortedTripTemplates,
    multiloadTripTemplates: multiloadTripTemplatesMap,
    rawTripTemplates: tripTemplates,
    tripTemplatesMap,
  };
}
